import { Fragment, useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode.react';
import { OrgId, SsoProviderName, UserSelf, SsoProviderType } from 'shared/models';
import { CheckField, TextField } from 'ui/shared/controls';
import { InfoMessage, ErrorMessage, SuccessMessage, WarningMessage } from 'ui/shared/messages';
import { PasswordStrength } from 'ui/shared/controls/passwordStrength/PasswordStrength';
import { adminRequiresTfa, totpIssuer } from 'util/env';
import { useSession, useForm, useOrgs, useTemplateKey } from 'hooks';
import { Permission } from 'shared/permissions';
import { OrgSelectField } from 'ui/shared/controls/orgSelectField/OrgSelectField';
import { Dictionary, omit } from 'lodash';
import { useSelector } from 'react-redux';
import { getOptInFeatures } from 'selectors/featuresSelectors';

require('./MyProfileForm.css');

export type Props = {
    user: UserSelf;
    busy: boolean;
    errorMessage: string;
    successMessage: string;
    onChange(user: UserSelf & { password: string }): void;
    onUnassume(): void;
};

type FormData = {
    displayName: string;
    emailAddress: string | null;
    phoneNumber: string;
    newPassword: string;
    tfaEnabled: boolean;
    tfaAuthCode?: string;
    orgId: OrgId | null;
    optIns: Dictionary<boolean>;
};

export const MyProfileForm = ({ user, busy, errorMessage, successMessage, onChange, onUnassume }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const { orgs, busy: orgsBusy } = useOrgs();

    const [ isChangingPassword, setChangingPassword ] = useState(false);

    let ssoProvider: SsoProviderType | null = null;

    const onSubmit = useCallback((data: FormData) => {
        onChange({
            ...user,
            ...omit(data, 'newPassword'),
            password: (isChangingPassword ? data.newPassword : undefined) as string,
        });
    }, [ user, isChangingPassword, onChange ]);

    const initialData = useMemo(() => ({
        // @ts-ignore:next-line: M1-1712: HACK: A logged in user (from before optIns existed) won't have optIns, so add it here. Can remove this hack after Prod has been upgraded
        optIns: {},
        ...user,
        newPassword: '',
    }), [ user ]);

    const { data, dirty, undo, handleChange, handleSubmit, setData } = useForm<FormData>('profile', initialData, onSubmit);

    const optInFeatures = useSelector(getOptInFeatures);
    const optInFeatureIds = Object.keys(optInFeatures);

    const otpAuthUrl = `otpauth://totp/${totpIssuer}:${user.login}?secret=${user.tfaSecret}&issuer=${totpIssuer}`;

    ssoProvider = orgs && data.orgId && orgs[data.orgId]?.ssoProvider || null;

    // Whether admins are required to have TFA enabled and Auth Code input (M1-2253)
    const shouldRequireTfa = adminRequiresTfa && (hasPermission(null, Permission.AllOrganizations) || hasPermission(null, Permission.Admin));
    const requiresTfaNotice = !user.tfaEnabled && shouldRequireTfa;
    useEffect(() => {
        if (shouldRequireTfa) {
            setData(data => ({
                ...data,
                tfaEnabled: true,
            }));
        }
    }, [ data.tfaEnabled, setData, shouldRequireTfa ]);

    // Visibility of QR code can be toggled on and off; defaults to off
    const [ qrCodeToggle, setQrCodeToggle ] = useState(!!requiresTfaNotice);
    const toggleQrCode = () => setQrCodeToggle(!qrCodeToggle);
    const shouldShowQrCode = !ssoProvider && data.tfaEnabled && qrCodeToggle;

    // Visibility of TFA auth code input
    const shouldShowAuthCode = !ssoProvider && (shouldRequireTfa || initialData.tfaEnabled || data.tfaEnabled);

    const managedByNodes = useTemplateKey('USERS.EDIT_DIALOG.PASSWORD_MANAGED_BY', () => ({
        SSO_NAME: <b>{ SsoProviderName[ssoProvider!] }</b>,
    }), [ ssoProvider ]);
    
    return (
        <form
            id="my-profile-form"
            onSubmit={ handleSubmit }
            className={ `ui form${errorMessage ? ' error' : ''}${busy || (user.orgId && orgsBusy) ? ' loading' : ''}` }
            style={ { width: '640px' } }
            autoComplete="off"
        >
            { user.realUserId &&
                <WarningMessage
                    message={
                        <>
                            <span>
                                { t('PROFILE.ASSUMED') }
                            </span>
                            <button
                                type="button"
                                id="revert-button"
                                className="ui button mini red"
                                onClick={ onUnassume }
                            >
                                { t('COMMAND.REVERT') }
                            </button>
                        </>
                    }
                />
            }
            { !!requiresTfaNotice &&
                <InfoMessage
                    messageKey="PROFILE.ADMIN_TFA_NOTICE"
                />
            }
            <div className="ui grid">
                <div className="six wide column">
                    <TextField
                        id="edit-user-display-name"
                        name="displayName"
                        labelKey="USERS.EDIT_DIALOG.FIELD.DISPLAY_NAME"
                        maxLength={ 32 }
                        value={ data.displayName || '' }
                        onChange={ handleChange }
                    />
                </div>
                { hasPermission(null, Permission.Admin) &&
                    <div className="six wide column">
                        <OrgSelectField
                            id="edit-user-org"
                            name="orgId"
                            labelKey="USERS.EDIT_DIALOG.FIELD.ORG"
                            value={ data.orgId }
                            disableSsoOrg={ true }
                            onChange={ handleChange }
                        />
                    </div>
                }
            </div>

            <div className="ui grid">
                <div className="ten wide column">
                    <TextField
                        id="edit-user-email-address"
                        name="emailAddress"
                        labelKey="USERS.EDIT_DIALOG.FIELD.EMAIL_ADDRESS"
                        maxLength={ 64 }
                        value={ data.emailAddress || '' }
                        onChange={ handleChange }
                        disabled={ !!ssoProvider }
                    />
                </div>
                <div className="six wide column">
                    <TextField
                        id="edit-user-phone-number"
                        name="phoneNumber"
                        labelKey="USERS.EDIT_DIALOG.FIELD.PHONE_NUMBER"
                        maxLength={ 16 }
                        value={ data.phoneNumber || '' }
                        onChange={ handleChange }
                    />
                </div>
            </div>

            { isChangingPassword && !ssoProvider &&
                <div className="ui grid">
                    <div className="six wide column">
                        <TextField
                            type="password"
                            autoComplete="new-password"
                            id="edit-user-password"
                            name="newPassword"
                            labelKey={ `USERS.EDIT_DIALOG.FIELD.NEW_PASSWORD` }
                            maxLength={ 64 }
                            value={ data.newPassword }
                            onChange={ handleChange }
                        />
                        <PasswordStrength
                            password={ data.newPassword }
                        />

                    </div>
                    <div className="five wide column">
                        <button
                            id="cancel-change-password"
                            type="button"
                            className="ui gray button"
                            onClick={ () => setChangingPassword(false) }
                        >
                            { t('COMMAND.CANCEL') }
                        </button>
                    </div>
                </div>
            }
            { !isChangingPassword &&
                <div className="ui grid">
                    <div className="six wide column">
                        { ssoProvider
                            ?
                                managedByNodes
                            :
                                <button
                                    id="change-password"
                                    type="button"
                                    className="ui gray button"
                                    onClick={ () => setChangingPassword(true) }
                                >
                                    { t('COMMAND.CHANGE_PASSWORD') }
                                </button>
                        }
                    </div>
                </div>
            }

            { !ssoProvider &&
                <div className="ui middle aligned grid">
                    <div className="eight wide column">
                        <CheckField
                            id="edit-user-tfa-enabled"
                            labelKey="USERS.EDIT_DIALOG.FIELD.TFA_ENABLED"
                            input={ {
                                name: "tfaEnabled",
                                checked: !!data.tfaEnabled,
                                onChange: handleChange,
                            } }
                            readOnly={ shouldRequireTfa }
                            disabled={ shouldRequireTfa }
                        />
                    </div>
                    { data.tfaEnabled &&
                        <div className="eight wide column">
                            <button
                                className="ui tiny gray button"
                                type="button"
                                onClick={ toggleQrCode }
                            >
                                { qrCodeToggle ? t('COMMAND.QR_CODE_HIDE') : t('COMMAND.QR_CODE_SHOW') }
                            </button>
                        </div>
                    }
                </div>
            }

            { shouldShowQrCode &&
                <div className="tfa">
                    <div
                        className="qrcode-host"
                        data-url={ otpAuthUrl }
                    >
                        <QRCode
                            value={ otpAuthUrl }
                            level="H"
                            renderAs="svg"
                            size={ 200 }
                        />
                    </div>
                    <div className="qrcode-instructions">
                        { t('USERS.EDIT_DIALOG.FIELD.TFA_INSTRUCTIONS') }
                    </div>
                </div>
            }

            { shouldShowAuthCode &&
                <div className="ui grid">
                    <div className="six wide column">
                        <TextField
                            type="text"
                            id="edit-user-tfa-auth-code"
                            name="tfaAuthCode"
                            labelKey="USERS.EDIT_DIALOG.FIELD.TFA_AUTH_CODE"
                            maxLength={ 6 }
                            value={ data.tfaAuthCode || '' }
                            onChange={ handleChange }
                        />
                    </div>
                </div>
            }

            { optInFeatureIds.length > 0 &&
                <>
                    <h4>
                        { t('FEATURE.OPT_IN') }
                    </h4>
                    { optInFeatureIds.map(featureId =>
                        <Fragment key={ featureId }>
                            <CheckField
                                id={ `edit-user-feature-${featureId.toLowerCase()}` }
                                labelKey={ `FEATURE.${featureId.toUpperCase()}_NAME` }
                                className="_feature"
                                input={ {
                                    name: `optIns.${featureId}`,
                                    checked: !!data.optIns[featureId],
                                    onChange: handleChange,
                                } }
                            />
                            <dd>
                                { t(`FEATURE.${featureId.toUpperCase()}_DESC`) }
                            </dd>
                        </Fragment>
                    ) }
                </>
            }

            <div style={ { marginTop: '30px' } }>
                <button
                    id="save-user-button"
                    className="ui primary button"
                    type="submit"
                    disabled={ busy || !dirty }
                >
                    { t('COMMAND.SAVE') }
                </button>

                { dirty &&
                    <button
                        className="ui button"
                        type="button"
                        onClick={ undo }
                        disabled={ busy }
                    >
                        { t('COMMAND.UNDO') }
                    </button>
                }
            </div>
            <SuccessMessage message={ successMessage }/>
            <ErrorMessage message={ errorMessage }/>
        </form>
    );
};
